import { Estimate, CalculatorState, MaterialOption } from './types'

export const handlePrint = (
  estimate: Estimate,
  state: CalculatorState,
  materialOptions: MaterialOption[]
) => {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return

  const materialLabel = materialOptions.find(m => m.value === state.material)?.label || state.material

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Roof Estimate - ${state.zipCode}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { background: #1E40AF; color: white; padding: 30px; text-align: center; margin-bottom: 30px; }
          .header h1 { margin: 0; font-size: 28px; }
          .section { margin: 20px 0; }
          .section h2 { font-size: 18px; color: #1E40AF; margin-bottom: 10px; border-bottom: 2px solid #1E40AF; padding-bottom: 5px; }
          .detail { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #E5E7EB; }
          .detail-label { font-weight: 600; color: #374151; }
          .detail-value { color: #6B7280; }
          .estimate-box { background: #F97316; color: white; padding: 30px; text-align: center; font-size: 32px; font-weight: bold; margin: 30px 0; border-radius: 8px; }
          .breakdown { background: #F9FAFB; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .breakdown-item { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
          .footer { text-align: center; color: #6B7280; font-size: 12px; margin-top: 40px; border-top: 1px solid #E5E7EB; padding-top: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Roof Cost Estimate</h1>
          <p style="margin: 10px 0 0 0; font-size: 14px; opacity: 0.9;">www.roofingcostscalculator.com</p>
        </div>
        
        <div class="section">
          <h2>Project Details</h2>
          <div class="detail"><span class="detail-label">Zip Code:</span><span class="detail-value">${state.zipCode}</span></div>
          <div class="detail"><span class="detail-label">Square Footage:</span><span class="detail-value">${state.squareFootage} sq ft</span></div>
          <div class="detail"><span class="detail-label">Material:</span><span class="detail-value">${materialLabel}</span></div>
          <div class="detail"><span class="detail-label">Remove Old Roof:</span><span class="detail-value">${state.removeOldRoof === 'yes' ? 'Yes' : 'No'}</span></div>
          ${state.removeOldRoof === 'yes' ? `<div class="detail"><span class="detail-label">Existing Layers:</span><span class="detail-value">${state.numLayers}</span></div>` : ''}
        </div>
        
        <div class="section">
          <h2>Property Characteristics</h2>
          <div class="detail"><span class="detail-label">Number of Stories:</span><span class="detail-value">${state.numStories}</span></div>
          <div class="detail"><span class="detail-label">Roof Pitch:</span><span class="detail-value">${state.roofPitch}</span></div>
          <div class="detail"><span class="detail-label">Roof Condition:</span><span class="detail-value">${state.roofCondition}</span></div>
          <div class="detail"><span class="detail-label">Chimneys:</span><span class="detail-value">${state.hasChimneys}</span></div>
          <div class="detail"><span class="detail-label">Skylights:</span><span class="detail-value">${state.hasSkylights}</span></div>
          <div class="detail"><span class="detail-label">Complexity:</span><span class="detail-value">${state.hasValleys} valleys</span></div>
          <div class="detail"><span class="detail-label">Timeline:</span><span class="detail-value">${state.urgency}</span></div>
        </div>
        
        <div class="breakdown">
          <h2 style="margin-top: 0;">Cost Breakdown</h2>
          <div class="breakdown-item"><span>Base Material & Installation:</span><span>Included</span></div>
          ${state.removeOldRoof === 'yes' && estimate.breakdown.tearOff > 0 ? `<div class="breakdown-item"><span>Tear Off (${state.numLayers} layer${state.numLayers > 1 ? 's' : ''}):</span><span>+$${estimate.breakdown.tearOff.toLocaleString()}</span></div>` : ''}
          ${state.roofPitch !== 'low' ? `<div class="breakdown-item"><span>Pitch Adjustment (${state.roofPitch}):</span><span>+${((estimate.breakdown.pitchMultiplier - 1) * 100).toFixed(0)}%</span></div>` : ''}
          ${state.numStories > 1 ? `<div class="breakdown-item"><span>Multi-Story Labor (${state.numStories} stories):</span><span>+${((estimate.breakdown.storiesMultiplier - 1) * 100).toFixed(0)}%</span></div>` : ''}
          ${state.hasChimneys > 0 ? `<div class="breakdown-item"><span>Chimney Flashing (${state.hasChimneys}):</span><span>+$${estimate.breakdown.chimneyFlashing.toLocaleString()}</span></div>` : ''}
          ${state.hasSkylights > 0 ? `<div class="breakdown-item"><span>Skylight Flashing (${state.hasSkylights}):</span><span>+$${estimate.breakdown.skylightFlashing.toLocaleString()}</span></div>` : ''}
          ${estimate.breakdown.complexityFee > 0 ? `<div class="breakdown-item"><span>Complexity Fee (${state.hasValleys} valleys):</span><span>+$${estimate.breakdown.complexityFee.toLocaleString()}</span></div>` : ''}
          ${estimate.breakdown.urgencyFee > 0 ? `<div class="breakdown-item"><span>Urgency Premium (${state.urgency}):</span><span>+$${estimate.breakdown.urgencyFee.toLocaleString()}</span></div>` : ''}
        </div>
        
        <div class="estimate-box">
          $${estimate.low.toLocaleString()} - $${estimate.high.toLocaleString()}
        </div>
        
        <div class="footer">
          <p><strong>Generated:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p style="margin-top: 10px;">This estimate is based on industry averages and provided information. Actual costs may vary based on location, contractor rates, permits, and unforeseen conditions. For accurate pricing, consult licensed roofing professionals.</p>
        </div>
      </body>
    </html>
  `)
  printWindow.document.close()
  printWindow.print()
}
